"use client"

import { useState } from "react"
import { ThumbsUp, ThumbsDown } from "lucide-react"
import { Message } from "./types"
import { FeedbackDialog } from "./FeedbackDialog"
import { getToolRenderer } from "@/hooks/useToolRenderer"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { useBranding } from "@/lib/branding"

interface ChatMessageProps {
  message: Message
  sessionId: string
  onFeedbackSubmit: (feedbackType: "positive" | "negative", comment: string) => Promise<void>
}

export function ChatMessage({
  message,
  sessionId: _sessionId,
  onFeedbackSubmit,
}: ChatMessageProps) {
  const branding = useBranding()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<"positive" | "negative">(
    "positive"
  )
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleFeedbackClick = (type: "positive" | "negative") => {
    setSelectedFeedbackType(type)
    setIsDialogOpen(true)
  }

  const handleFeedbackSubmit = async (comment: string) => {
    await onFeedbackSubmit(selectedFeedbackType, comment)
    setFeedbackSubmitted(true)
  }

  const renderAssistantContent = () => {
    const hasSegments = Boolean(message.segments?.length)
    if (!hasSegments && !message.content) {
      return <TypingIndicator />
    }
    if (hasSegments) {
      return message.segments!.map((seg, i) => {
        if (seg.type === "text") {
          return <MarkdownRenderer key={i} content={seg.content} />
        }
        const render = getToolRenderer(seg.toolCall.name)
        if (!render) return null
        return (
          <div key={seg.toolCall.toolUseId} className="my-1">
            {render({
              name: seg.toolCall.name,
              args: seg.toolCall.input,
              status: seg.toolCall.status,
              result: seg.toolCall.result,
            })}
          </div>
        )
      })
    }
    return <MarkdownRenderer content={message.content} />
  }

  const isUser = message.role === "user"

  return (
    <div className={`group flex flex-col ${isUser ? "items-end" : "items-start"} animate-fade-in`}>
      {!isUser && (
        <div className="mb-2 flex items-center gap-2">
          {/* Vendor monogram: the first letter of the configured vendor name,
              which is "d" on the shipped Devoteam defaults. */}
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-coral text-[10px] font-bold text-white">
            {branding.vendorName.charAt(0).toLowerCase()}
          </span>
          <span className="label-mono">{branding.productName}</span>
        </div>
      )}

      {/* Quiet role distinction: the user gets the one coral bubble on the
          e2 ambient token, the assistant stays on the page as set prose. */}
      <div
        className={`min-w-0 max-w-[85%] break-words leading-relaxed ${
          isUser
            ? "whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand-coral px-4 py-3 text-white shadow-e2"
            : "text-brand-ink"
        }`}
      >
        {isUser ? message.content : renderAssistantContent()}
      </div>

      <div className="mt-1.5 flex items-center gap-2 px-1">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
          {formatTime(message.timestamp)}
        </span>

        {!isUser && message.content && (
          <div className="ml-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              onClick={() => handleFeedbackClick("positive")}
              disabled={feedbackSubmitted}
              className="rounded-md p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral/50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Positive feedback"
              title="Good response"
            >
              <ThumbsUp size={13} />
            </button>
            <button
              onClick={() => handleFeedbackClick("negative")}
              disabled={feedbackSubmitted}
              className="rounded-md p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-brand-coral-wash hover:text-brand-coral-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral/50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Negative feedback"
              title="Bad response"
            >
              <ThumbsDown size={13} />
            </button>
            {feedbackSubmitted && (
              <span className="ml-1.5 text-[11px] text-muted-foreground">Thanks, noted.</span>
            )}
          </div>
        )}
      </div>

      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
      />
    </div>
  )
}

function TypingIndicator() {
  const branding = useBranding()
  return (
    <span
      className="inline-flex items-center gap-1.5 py-2"
      role="status"
      aria-label={`${branding.productName} is thinking`}
    >
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-coral" />
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-coral" />
      <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-coral" />
    </span>
  )
}
